import type {
  IBenchmarkRate,
  IBenchmarkRateList,
  IPublishedRate,
  IPublishedRateHistory,
  IPublishedRateLeg,
  IRateDefinitionGroup,
  IRateDefinitionLeg,
  IRateDefinitionLegInput,
  IRateDefinitionList,
  IRateDefinitionPutRequest,
} from "@pulse-ui/codegen-v2/generated_ts/rate_definition";
import { api } from "../base";

export type {
  IBenchmarkRate,
  IBenchmarkRateList,
  IPublishedRate,
  IPublishedRateHistory,
  IPublishedRateLeg,
  IRateDefinitionGroup,
  IRateDefinitionLeg,
  IRateDefinitionLegInput,
  IRateDefinitionList,
  IRateDefinitionPutRequest,
};
export { RateSource } from "@pulse-ui/codegen-v2/generated_ts/rate_definition";

const rateDefinitionsEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** Fetches every published rate definition with its legs. */
    getRateDefinitions: build.query<IRateDefinitionList, void>({
      query: () => ({ url: "/api/trading-ops/v1/rate-definitions" }),
      providesTags: ["RateDefinitions"],
    }),

    /** Replaces a rate's whole leg list, creating the rate if absent. */
    putRateDefinition: build.mutation<
      IRateDefinitionGroup,
      { rateName: string; body: IRateDefinitionPutRequest }
    >({
      query: ({ rateName, body }) => ({
        url: `/api/trading-ops/v1/rate-definitions/${encodeURIComponent(rateName)}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["RateDefinitions"],
    }),

    /** Deletes a rate and all of its legs. */
    deleteRateDefinition: build.mutation<IRateDefinitionGroup, string>({
      query: (rateName) => ({
        url: `/api/trading-ops/v1/rate-definitions/${encodeURIComponent(rateName)}`,
        method: "DELETE",
      }),
      invalidatesTags: ["RateDefinitions"],
    }),

    /** A rate's retained publications on digital.rates.daily, newest first; server-cached. */
    getPublishedRates: build.query<IPublishedRateHistory, string>({
      query: (rateName) => ({
        url: "/api/trading-ops/v1/published-rates",
        params: { rate_name: rateName },
      }),
      keepUnusedDataFor: 300,
    }),

    /** Every FACT benchmark's latest rate on the benchmark-rate topic; server-cached. */
    getBenchmarkRates: build.query<IBenchmarkRateList, void>({
      query: () => ({ url: "/api/trading-ops/v1/benchmark-rates" }),
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useGetRateDefinitionsQuery,
  usePutRateDefinitionMutation,
  useDeleteRateDefinitionMutation,
  useGetPublishedRatesQuery,
  useGetBenchmarkRatesQuery,
} = rateDefinitionsEndpoints;
